import { Player, Match, Season, AdminSettings } from '../types';

// API endpoint
const API_URL = process.env.REACT_APP_API_URL || '/api.php';

// LocalStorage fallback (sadece admin mode için)
const STORAGE_KEYS = {
  ADMIN_MODE: 'futbol_admin_mode',
};

// Cache için state
let cache: {
  players?: Player[];
  matches?: Match[];
  seasons?: Season[];
  currentSeason?: string;
  adminSettings?: AdminSettings;
  [key: string]: any;
} = {};

// localStorage cache key'leri
const CACHE_KEYS = {
  PLAYERS: 'futbol_cache_players',
  MATCHES: 'futbol_cache_matches',
  SEASONS: 'futbol_cache_seasons',
  CURRENT_SEASON: 'futbol_cache_currentSeason',
  ADMIN_SETTINGS: 'futbol_cache_adminSettings',
  CACHE_TIMESTAMP: 'futbol_cache_timestamp',
};

// Cache TTL - Tüm cihazlarda aynı (senkronizasyon için)
const CACHE_TTL = 5 * 60 * 1000; // 5 dakika (tüm cihazlarda aynı)

// Oyuncu kayıtlarını sıraya al: silme + maç istatistiği aynı anda çalışınca eski listeyle üzerine yazmayı önler
let savePlayersMutex: Promise<void> = Promise.resolve();

// localStorage'dan cache oku
const getFromLocalStorage = <T>(key: string): T | null => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`LocalStorage read error for ${key}:`, error);
    return null;
  }
};

// localStorage'a cache yaz
const saveToLocalStorage = <T>(key: string, value: T): void => {
  try {
    // ÖNEMLİ: Base64 görselleri localStorage'a kaydetme (quota aşımı önlemek için)
    const serialized = JSON.stringify(value);
    const isBase64Image = typeof value === 'string' && value.startsWith('data:image');
    
    // Base64 görselleri localStorage'a kaydetme
    if (isBase64Image && (key.includes('squadImage') || key.includes('homePageImage') || key.includes('matchdayImage'))) {
      console.warn(`[Storage] Base64 görsel localStorage'a kaydedilmeyecek (quota aşımı önlemek için): ${key}`);
      return; // Base64 görselleri localStorage'a kaydetme
    }
    
    localStorage.setItem(key, serialized);
  } catch (error: any) {
    console.error(`LocalStorage write error for ${key}:`, error);
    
    // QuotaExceededError durumunda eski cache'leri temizle
    if (error?.name === 'QuotaExceededError' || error?.message?.includes('quota')) {
      console.warn('[Storage] LocalStorage dolu, eski cache temizleniyor...');
      try {
        // ÖNEMLİ: Base64 görselleri önce temizle (en çok yer kaplayan)
        const base64Keys = ['futbol_cache_squadImage', 'futbol_cache_homePageImage', 'futbol_cache_matchdayImage'];
        for (const base64Key of base64Keys) {
          try {
            const cached = localStorage.getItem(base64Key);
            if (cached && cached.startsWith('data:image')) {
              localStorage.removeItem(base64Key);
              console.log(`[Storage] Base64 cache temizlendi: ${base64Key}`);
            }
          } catch (e) {
            // Temizleme hatası önemli değil
          }
        }
        
        // En eski cache'leri temizle (oyuncular hariç - kritik veriler)
        if (key !== CACHE_KEYS.PLAYERS && key !== CACHE_KEYS.MATCHES) {
          localStorage.removeItem(key);
        }
        // Tekrar dene (daha küçük veri için)
        if (key !== CACHE_KEYS.PLAYERS && key !== CACHE_KEYS.MATCHES) {
          const smallerValue = Array.isArray(value) ? value.slice(0, 10) : value;
          localStorage.setItem(key, JSON.stringify(smallerValue));
        }
      } catch (retryError) {
        console.error('[Storage] Cache temizleme hatası:', retryError);
      }
    }
  }
};

// Cache'in geçerli olup olmadığını kontrol et
const isCacheValid = (): boolean => {
  const timestamp = getFromLocalStorage<number>(CACHE_KEYS.CACHE_TIMESTAMP);
  if (!timestamp) return false;
  return Date.now() - timestamp < CACHE_TTL;
};

/** Tam liste kaydı öncesi kullan: bellek/localStorage ve tarayıcı GET önbelleğini atlar (silinen oyuncunun geri gelmesini önler). */
export type GetPlayersOptions = { forceRefresh?: boolean };

// Hosting API'den veri okuma helper (cache ile)
// ÖNEMLİ: fetch cache: 'no-store' — tarayıcı api.php GET yanıtını diskte önbelleğe almasın (eski oyuncu listesi yazılmasın)
const getFromAPI = async <T>(
  path: string,
  defaultValue: T,
  cacheKey?: string,
  options?: GetPlayersOptions
): Promise<T> => {
  const forceRefresh = options?.forceRefresh === true;
  try {
    if (forceRefresh && cacheKey) {
      delete cache[cacheKey];
      const lsKey = CACHE_KEYS[cacheKey as keyof typeof CACHE_KEYS];
      if (lsKey) {
        try {
          localStorage.removeItem(lsKey);
        } catch {
          /* ignore */
        }
      }
    }
    const bust = forceRefresh ? `&_=${Date.now()}` : '';
    console.log(`[Storage] Fetching from API: ${path}${forceRefresh ? ' (forceRefresh)' : ''}`);
    const response = await fetch(`${API_URL}?path=${path}${bust}`, { cache: 'no-store' });
    
    if (!response.ok) {
      // API hatası durumunda cache'den dön
      console.warn(`[Storage] API Error (${path}): ${response.status}, using cache as fallback`);
      
      // Önce memory cache'den kontrol et
      if (cacheKey && cache[cacheKey] !== undefined) {
        console.log(`[Storage] Using memory cache (fallback) for ${path}`);
        return cache[cacheKey] as T;
      }
      
      // Sonra localStorage cache'den kontrol et
      if (cacheKey) {
        const localStorageKey = CACHE_KEYS[cacheKey as keyof typeof CACHE_KEYS];
        if (localStorageKey) {
          const cachedValue = getFromLocalStorage<T>(localStorageKey);
          if (cachedValue !== null) {
            console.log(`[Storage] Using localStorage cache (fallback) for ${path}`);
            return cachedValue;
          }
        }
      }
      
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const result = await response.json();
    if (!result.success) {
      // API hatası durumunda cache'den dön
      console.warn(`[Storage] API Error (${path}): ${result.error}, using cache as fallback`);
      
      // Önce memory cache'den kontrol et
      if (cacheKey && cache[cacheKey] !== undefined) {
        console.log(`[Storage] Using memory cache (fallback) for ${path}`);
        return cache[cacheKey] as T;
      }
      
      // Sonra localStorage cache'den kontrol et
      if (cacheKey) {
        const localStorageKey = CACHE_KEYS[cacheKey as keyof typeof CACHE_KEYS];
        if (localStorageKey) {
          const cachedValue = getFromLocalStorage<T>(localStorageKey);
          if (cachedValue !== null) {
            console.log(`[Storage] Using localStorage cache (fallback) for ${path}`);
            return cachedValue;
          }
        }
      }
      
      throw new Error(result.error || 'API request failed');
    }
    
    const value = result.data !== undefined ? result.data : defaultValue;
    console.log(`[Storage] Fetched from API (${path}):`, Array.isArray(value) ? `${value.length} items` : value);
    
    // API'den başarıyla alındı, cache'i güncelle
    if (cacheKey) {
      cache[cacheKey] = value;
      
      // localStorage'a da kaydet
      const localStorageKey = CACHE_KEYS[cacheKey as keyof typeof CACHE_KEYS];
      if (localStorageKey) {
        saveToLocalStorage(localStorageKey, value);
        saveToLocalStorage(CACHE_KEYS.CACHE_TIMESTAMP, Date.now());
      }
    }
    
    return value;
  } catch (error) {
    console.error(`[Storage] API read error for ${path}:`, error);
    
    // Hata durumunda önce memory cache'den dön
    if (cacheKey && cache[cacheKey] !== undefined) {
      console.log(`[Storage] Using memory cache (fallback) for ${path}`);
      return cache[cacheKey] as T;
    }
    
    // Sonra localStorage cache'den dön (geçerli olmasa bile)
    if (cacheKey) {
      const localStorageKey = CACHE_KEYS[cacheKey as keyof typeof CACHE_KEYS];
      if (localStorageKey) {
        const cachedValue = getFromLocalStorage<T>(localStorageKey);
        if (cachedValue !== null) {
          console.log(`[Storage] Using localStorage cache (fallback) for ${path}`);
          return cachedValue;
        }
      }
    }
    
    console.log(`[Storage] Returning default value for ${path}`);
    return defaultValue;
  }
};

// Hosting API'ye veri yazma helper
const saveToAPI = async (path: string, data: any, cacheKey?: string): Promise<void> => {
  try {
    console.log(`[Storage] Saving to API: ${path}`, data);
    const response = await fetch(`${API_URL}?path=${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API Save Error (${path}):`, response.status, errorText);
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || 'Unknown error' };
      }
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const result = await response.json();
    if (!result.success) {
      console.error(`API Save Error (${path}):`, result.error || 'Save failed');
      throw new Error(result.error || 'Save failed');
    }
    console.log(`[Storage] Saved successfully: ${path}`);
    
    // Cache'i güncelle (sadece başarılı kayıt sonrası)
    if (cacheKey && result.success) {
      cache[cacheKey] = data;
      
      // localStorage'a da kaydet
      const localStorageKey = CACHE_KEYS[cacheKey as keyof typeof CACHE_KEYS];
      if (localStorageKey) {
        saveToLocalStorage(localStorageKey, data);
        saveToLocalStorage(CACHE_KEYS.CACHE_TIMESTAMP, Date.now());
      }
    }
  } catch (error) {
    console.error(`API write error for ${path}:`, error);
    throw error;
  }
};

export const storage = {
  // Players - Async
  getPlayers: async (options?: GetPlayersOptions): Promise<Player[]> => {
    const players = await getFromAPI<Player[]>('players', [], 'players', options);
    cache.players = players;
    return players;
  },

  savePlayers: async (players: Player[]): Promise<void> => {
    const execute = async (): Promise<void> => {
      console.log(`[Storage] savePlayers çağrıldı - ${players.length} oyuncu kaydediliyor`);

      // Görselleri birleştirmeden önce sunucudan taze liste al
      let existingPlayers: Player[] = [];
      try {
        existingPlayers = await getFromAPI<Player[]>('players', [], 'players', { forceRefresh: true });
        console.log(`[Storage] Mevcut oyuncular API'den çekildi: ${existingPlayers.length} oyuncu`);
      } catch (error) {
        console.warn('[Storage] Mevcut oyuncular çekilemedi, devam ediliyor:', error);
      }

      const existingById = new Map(existingPlayers.map(ep => [ep.id, ep]));

      const validatedPlayers = players.map(p => {
        const existingPlayer = existingById.get(p.id);

        const validated = {
          id: p.id,
          name: p.name || '',
          position: p.position || '',
          code: p.code || '',
          image: p.image || existingPlayer?.image,
          profileImage: p.profileImage || existingPlayer?.profileImage,
          playerCard: p.playerCard || existingPlayer?.playerCard,
          playerCards: p.playerCards || existingPlayer?.playerCards || (Array.isArray(p.playerCards) ? [] : {}),
          stats: p.stats || existingPlayer?.stats || {},
          badges: p.badges || existingPlayer?.badges,
        };

        if (existingPlayer?.profileImage && !p.profileImage && !validated.profileImage) {
          console.warn('[Storage] UYARI: profileImage kayboldu, mevcut değerden geri yükleniyor!', {
            playerId: p.id,
            playerName: p.name,
            existingProfileImage: existingPlayer.profileImage,
          });
          validated.profileImage = existingPlayer.profileImage;
        }

        if (existingPlayer?.playerCard && !p.playerCard && !validated.playerCard) {
          console.warn('[Storage] UYARI: playerCard kayboldu, mevcut değerden geri yükleniyor!', {
            playerId: p.id,
            playerName: p.name,
          });
          validated.playerCard = existingPlayer.playerCard;
        }

        if (existingPlayer?.playerCards && !p.playerCards && !validated.playerCards) {
          console.warn('[Storage] UYARI: playerCards kayboldu, mevcut değerden geri yükleniyor!', {
            playerId: p.id,
            playerName: p.name,
          });
          validated.playerCards = existingPlayer.playerCards;
        }

        return validated;
      });

      await saveToAPI('players', validatedPlayers, 'players');

      cache.players = validatedPlayers;
      saveToLocalStorage(CACHE_KEYS.PLAYERS, validatedPlayers);
      saveToLocalStorage(CACHE_KEYS.CACHE_TIMESTAMP, Date.now());

      console.log(`[Storage] savePlayers başarılı - ${validatedPlayers.length} oyuncu kaydedildi`);
    };

    const done = savePlayersMutex.then(execute);
    savePlayersMutex = done.catch(() => {});
    return done;
  },

  // Players için realtime listener (simüle edilmiş - polling)
  onPlayersChange: (callback: (players: Player[]) => void): (() => void) => {
    let intervalId: NodeJS.Timeout | null = null;
    
    const poll = async () => {
      try {
        const players = await storage.getPlayers();
        callback(players);
      } catch (error) {
        console.error('Polling error:', error);
      }
    };
    
    // İlk çağrı
    poll();
    
    // Her 5 saniyede bir kontrol et
    intervalId = setInterval(poll, 5000);
    
    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  },

  // Matches - Async
  getMatches: async (): Promise<Match[]> => {
    const matches = await getFromAPI<Match[]>('matches', [], 'matches');
    cache.matches = matches;
    return matches;
  },

  saveMatches: async (matches: Match[]): Promise<void> => {
    try {
      console.log(`[Storage] saveMatches çağrıldı - ${matches.length} maç kaydediliyor`);
      
      // ÖNEMLİ: Önce API'ye kaydet, başarılı olursa cache'i güncelle
      // Bu sayede hata durumunda eski cache korunur
      await saveToAPI('matches', matches, 'matches');
      
      // API'den başarıyla kaydedildikten sonra cache'i güncelle
      cache.matches = matches;
      
      // ÖNEMLİ: Cache'i güncelle - hem memory hem localStorage
      // Timestamp'i güncel yap ki cache geçerli olsun ama API'den de kontrol edilsin
      saveToLocalStorage(CACHE_KEYS.MATCHES, matches);
      saveToLocalStorage(CACHE_KEYS.CACHE_TIMESTAMP, Date.now()); // Cache'i güncel yap
      
      console.log(`[Storage] saveMatches başarılı - ${matches.length} maç kaydedildi`);
    } catch (error) {
      console.error('[Storage] saveMatches hatası:', error);
      // Hata durumunda cache'i koruyoruz (zaten güncellenmedi)
      throw error;
    }
  },

  // Matches için realtime listener (simüle edilmiş - polling)
  onMatchesChange: (callback: (matches: Match[]) => void): (() => void) => {
    let intervalId: NodeJS.Timeout | null = null;
    
    const poll = async () => {
      try {
        const matches = await storage.getMatches();
        callback(matches);
      } catch (error) {
        console.error('Polling error:', error);
      }
    };
    
    // İlk çağrı
    poll();
    
    // Her 5 saniyede bir kontrol et
    intervalId = setInterval(poll, 5000);
    
    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  },

  // Seasons - Async
  getSeasons: async (): Promise<Season[]> => {
    const seasons = await getFromAPI<Season[]>('seasons', [], 'seasons');
    if (seasons.length === 0) {
      // İlk sezonu oluştur
      const defaultSeason: Season = {
        id: 'season-1',
        name: '1. Sezon',
        isActive: true,
      };
      await storage.saveSeasons([defaultSeason]);
      return [defaultSeason];
    }
    cache.seasons = seasons;
    return seasons;
  },

  saveSeasons: async (seasons: Season[]): Promise<void> => {
    try {
      console.log(`[Storage] saveSeasons çağrıldı - ${seasons.length} sezon kaydediliyor`);
      
      // ÖNEMLİ: Önce API'ye kaydet, başarılı olursa cache'i güncelle
      await saveToAPI('seasons', seasons, 'seasons');
      
      // API'den başarıyla kaydedildikten sonra cache'i güncelle
      cache.seasons = seasons;
      
      // ÖNEMLİ: Cache'i güncelle - hem memory hem localStorage
      // Timestamp'i güncel yap ki cache geçerli olsun ama API'den de kontrol edilsin
      saveToLocalStorage(CACHE_KEYS.SEASONS, seasons);
      saveToLocalStorage(CACHE_KEYS.CACHE_TIMESTAMP, Date.now()); // Cache'i güncel yap
      
      console.log(`[Storage] saveSeasons başarılı - ${seasons.length} sezon kaydedildi`);
    } catch (error) {
      console.error('[Storage] saveSeasons hatası:', error);
      // Hata durumunda cache'i koruyoruz (zaten güncellenmedi)
      throw error;
    }
  },

  getCurrentSeason: async (): Promise<string> => {
    const seasonId = await getFromAPI<string>('currentSeason', '');
    if (seasonId) {
      cache.currentSeason = seasonId;
      return seasonId;
    }
    const seasons = await storage.getSeasons();
    const activeSeason = seasons.find((s: Season) => s.isActive);
    const result = activeSeason?.id || seasons[0]?.id || '';
    cache.currentSeason = result;
    return result;
  },

  setCurrentSeason: async (seasonId: string): Promise<void> => {
    await saveToAPI('currentSeason', seasonId, 'currentSeason');
    const seasons = await storage.getSeasons();
    const updatedSeasons = seasons.map((s: Season) => ({
      ...s,
      isActive: s.id === seasonId,
    }));
    await storage.saveSeasons(updatedSeasons);
  },

  // Admin Mode (localStorage'da kalıyor - kullanıcı bazlı)
  isAdminMode: (): boolean => {
    return localStorage.getItem(STORAGE_KEYS.ADMIN_MODE) === 'true';
  },

  setAdminMode: (isAdmin: boolean): void => {
    localStorage.setItem(STORAGE_KEYS.ADMIN_MODE, isAdmin.toString());
  },

  // Admin Settings - Async
  getAdminSettings: async (): Promise<AdminSettings> => {
    const settings = await getFromAPI<AdminSettings>('adminSettings', {}, 'adminSettings');
    cache.adminSettings = settings;
    return settings;
  },

  saveAdminSettings: async (settings: AdminSettings): Promise<void> => {
    // Cache'i önce güncelle
    cache.adminSettings = settings;
    
    // localStorage cache'i de güncelle
    saveToLocalStorage(CACHE_KEYS.ADMIN_SETTINGS, settings);
    saveToLocalStorage(CACHE_KEYS.CACHE_TIMESTAMP, Date.now());
    
    // API'ye kaydet
    await saveToAPI('adminSettings', settings, 'adminSettings');
    
    // Cache'i tekrar güncelle (API'den dönen veri ile)
    cache.adminSettings = settings;
  },

  // Field Images (Kadro sayfası için) - Async
  getFieldImages: async (): Promise<{ [key: string]: { image: string; playerCode: string } }> => {
    return await getFromAPI<{ [key: string]: { image: string; playerCode: string } }>(
      'fieldImages',
      {}
    );
  },

  saveFieldImages: async (images: { [key: string]: { image: string; playerCode: string } }): Promise<void> => {
    await saveToAPI('fieldImages', images);
  },

  // Squad Image (Kadro görseli) - Async
  getSquadImage: async (): Promise<string> => {
    // ÖNEMLİ: Base64 görselleri localStorage'a kaydetme (quota aşımı önlemek için)
    // Önce cache'den kontrol et (sadece URL formatındaki görseller cache'lenir)
    const cached = getFromLocalStorage<string>('futbol_cache_squadImage');
    const isCachedBase64 = cached && cached.startsWith('data:image');
    
    // Eğer cache'de base64 varsa, cache'i temizle (eski format)
    if (isCachedBase64) {
      try {
        localStorage.removeItem('futbol_cache_squadImage');
        console.log('[Storage] getSquadImage - Eski base64 cache temizlendi');
      } catch (e) {
        // Cache temizleme hatası önemli değil
      }
    }
    
    // Retry mekanizması ile database lock sorunlarını önle
    const maxRetries = 3;
    let lastError: any = null;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        const image = await getFromAPI<string>('squadImage', '');
        // Başarılı okuma sonrası cache'i güncelle (sadece URL ise, base64 değilse)
        if (image && image.trim() !== '' && !image.startsWith('data:image')) {
          try {
            saveToLocalStorage('futbol_cache_squadImage', image);
          } catch (cacheError: any) {
            // Cache hatası önemli değil, devam et
            console.warn('[Storage] getSquadImage - Cache yazma hatası (önemsiz):', cacheError);
          }
        }
        return image;
      } catch (error: any) {
        lastError = error;
        if (error?.message?.includes('locked') && i < maxRetries - 1) {
          console.warn(`[Storage] getSquadImage - Database locked, retrying... (${i + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, 500 * (i + 1)));
          continue;
        }
        // Hata durumunda cache'den dön (sadece URL formatındaki görseller)
        if (cached && cached.trim() !== '' && !cached.startsWith('data:image')) {
          console.log('[Storage] getSquadImage - Using cache due to error');
          return cached;
        }
        throw error;
      }
    }
    
    // Tüm retry'lar başarısız oldu, cache'den dön (sadece URL formatındaki görseller)
    if (cached && cached.trim() !== '' && !cached.startsWith('data:image')) {
      console.log('[Storage] getSquadImage - Using cache after all retries failed');
      return cached;
    }
    
    throw lastError || new Error('getSquadImage failed after retries');
  },

  saveSquadImage: async (image: string): Promise<void> => {
    // ÖNEMLİ: Base64 görselleri localStorage'a kaydetme (quota aşımı önlemek için)
    // Sadece URL formatındaki görselleri cache'le
    const isBase64 = image.startsWith('data:image');
    
    // Retry mekanizması ile database lock sorunlarını önle
    const maxRetries = 3;
    let lastError: any = null;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        await saveToAPI('squadImage', image);
        // Başarılı kayıt sonrası cache'i güncelle (sadece URL ise)
        if (!isBase64) {
          try {
            saveToLocalStorage('futbol_cache_squadImage', image);
          } catch (cacheError: any) {
            // Cache hatası önemli değil, devam et
            console.warn('[Storage] saveSquadImage - Cache yazma hatası (önemsiz):', cacheError);
          }
        }
        console.log('[Storage] saveSquadImage başarılı');
        return;
      } catch (error: any) {
        lastError = error;
        if (error?.message?.includes('locked') && i < maxRetries - 1) {
          console.warn(`[Storage] saveSquadImage - Database locked, retrying... (${i + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, 500 * (i + 1)));
          continue;
        }
        // Diğer hatalar için cache'e kaydet (sadece URL ise)
        if (!isBase64) {
          try {
            saveToLocalStorage('futbol_cache_squadImage', image);
          } catch (cacheError: any) {
            console.warn('[Storage] saveSquadImage - Cache yazma hatası (önemsiz):', cacheError);
          }
        }
        throw error;
      }
    }
    
    // Tüm retry'lar başarısız oldu
    throw lastError || new Error('saveSquadImage failed after retries');
  },

  // Squad Markers (Kadro üzerindeki işaretçiler) - Async
  getSquadMarkers: async (): Promise<Array<{ id: string; x: number; y: number; width: number; height: number; playerCode: string }>> => {
    return await getFromAPI<Array<{ id: string; x: number; y: number; width: number; height: number; playerCode: string }>>(
      'squadMarkers',
      []
    );
  },

  saveSquadMarkers: async (markers: Array<{ id: string; x: number; y: number; width: number; height: number; playerCode: string }>): Promise<void> => {
    await saveToAPI('squadMarkers', markers);
  },

  // PWA Icons - Async
  getPwaIcon192: async (): Promise<string> => {
    return await getFromAPI<string>('pwaIcon192', '');
  },

  savePwaIcon192: async (icon: string): Promise<void> => {
    await saveToAPI('pwaIcon192', icon);
  },

  getPwaIcon512: async (): Promise<string> => {
    return await getFromAPI<string>('pwaIcon512', '');
  },

  savePwaIcon512: async (icon: string): Promise<void> => {
    await saveToAPI('pwaIcon512', icon);
  },

  // Hosting'e görsel yükleme
  uploadImage: async (file: File, path: string): Promise<string> => {
    try {
      console.log(`[Storage] Hosting'e yükleniyor: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB) -> ${path}`);
      
      // Klasör yapısını belirle (path'ten)
      let folder = 'general';
      if (path.includes('matchday')) {
        folder = 'matchday';
      } else if (path.includes('pwa-icons')) {
        folder = 'pwa-icons';
      } else if (path.includes('links/icons')) {
        folder = 'links/icons';
      } else if (path.includes('players')) {
        folder = 'players';
      } else if (path.includes('matches')) {
        folder = 'matches';
      } else if (path.includes('squad')) {
        folder = 'squad';
      }
      
      // FormData oluştur
      const formData = new FormData();
      formData.append('image', file);
      formData.append('folder', folder);
      
      // Upload endpoint URL'i
      const uploadUrl = process.env.REACT_APP_UPLOAD_URL || '/upload.php';
      
      console.log(`[Storage] Hosting endpoint'ine gönderiliyor: ${uploadUrl}`);
      
      // Hosting'e yükle
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        // 404 hatası için özel mesaj
        if (response.status === 404) {
          throw new Error('Upload endpoint bulunamadı! Lütfen upload.php dosyasının doğru yerde olduğundan emin olun.');
        }
        const errorData = await response.json().catch(() => ({ error: 'Bilinmeyen hata' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (!result.success || !result.url) {
        console.error('[Storage] Upload hatası:', result);
        throw new Error(result.error || 'Yükleme başarısız');
      }
      
      console.log(`[Storage] Yükleme tamamlandı: ${result.url}`);
      console.log(`[Storage] Upload detayları:`, {
        url: result.url,
        path: result.path,
        debug: result.debug
      });
      
      // URL'in geçerli olduğunu kontrol et
      if (!result.url || !result.url.startsWith('http')) {
        console.error('[Storage] Geçersiz URL formatı:', result.url);
        throw new Error('Geçersiz URL formatı döndü');
      }
      
      return result.url;
    } catch (error: any) {
      console.error(`[Storage] Yükleme hatası (${file.name}):`, error);
      
      // Daha anlaşılır hata mesajları
      let errorMessage = error?.message || String(error);
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        errorMessage = 'Sunucuya bağlanılamadı! İnternet bağlantınızı kontrol edin.';
      } else if (errorMessage.includes('400') || errorMessage.includes('invalid')) {
        errorMessage = 'Geçersiz dosya tipi! Sadece görsel dosyaları yükleyebilirsiniz.';
      }
      
      const enhancedError = new Error(errorMessage);
      (enhancedError as any).originalError = error;
      throw enhancedError;
    }
  },

  // Tüm verileri temizle
  clearAllData: async (): Promise<void> => {
    await Promise.all([
      saveToAPI('players', []),
      saveToAPI('matches', []),
      saveToAPI('seasons', []),
      saveToAPI('currentSeason', ''),
      saveToAPI('adminSettings', {}),
      saveToAPI('fieldImages', {}),
      saveToAPI('squadImage', ''),
      saveToAPI('squadMarkers', []),
      saveToAPI('pwaIcon192', ''),
      saveToAPI('pwaIcon512', ''),
    ]);
    cache = {};
  },

  // Cache'i başlat (uygulama başlangıcında çağrılmalı)
  initializeCache: async (): Promise<void> => {
    try {
      // Önce localStorage'dan yükle (hızlı başlangıç için)
      if (isCacheValid()) {
        cache.players = getFromLocalStorage<Player[]>(CACHE_KEYS.PLAYERS) || [];
        cache.matches = getFromLocalStorage<Match[]>(CACHE_KEYS.MATCHES) || [];
        cache.seasons = getFromLocalStorage<Season[]>(CACHE_KEYS.SEASONS) || [];
        cache.currentSeason = getFromLocalStorage<string>(CACHE_KEYS.CURRENT_SEASON) || '';
        cache.adminSettings = getFromLocalStorage<AdminSettings>(CACHE_KEYS.ADMIN_SETTINGS) || {};
      }
      
      // Sonra API'den güncel verileri çek (background'da)
      Promise.all([
        storage.getPlayers(),
        storage.getMatches(),
        storage.getSeasons(),
        storage.getCurrentSeason(),
        storage.getAdminSettings(),
      ]).then(([players, matches, seasons, currentSeason, adminSettings]) => {
        cache.players = players;
        cache.matches = matches;
        cache.seasons = seasons;
        cache.currentSeason = currentSeason;
        cache.adminSettings = adminSettings;
      }).catch(error => {
        console.error('Cache background update error:', error);
      });
    } catch (error) {
      console.error('Cache initialization error:', error);
    }
  },
  
  // Cache'i temizle
  clearCache: (): void => {
    cache = {};
    Object.values(CACHE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  },

  // Tüm cache'leri temizle ve sayfayı yenile (Service Worker + localStorage + memory)
  forceRefreshData: (): void => {
    storage.clearCache();
    // Service Worker cache'ini de temizle
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.active) {
          registration.active.postMessage({ type: 'CLEAR_CACHE' });
        }
      });
    }
    window.location.reload();
  },
};

export const ADMIN_PASSWORD = 'Lonca_FR_10';
